import React from 'react';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Card } from '@uhg-abyss/web/ui/Card';
import { DataTable } from '@uhg-abyss/web/ui/DataTable';
import { Button } from '@uhg-abyss/web/ui/Button';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { SelectInput } from '@uhg-abyss/web/ui/SelectInput';
import { Text } from '@uhg-abyss/web/ui/Text';
import { Tooltip } from '@uhg-abyss/web/ui/Tooltip';
import { Router } from '@uhg-abyss/web/ui/Router';
import { createColumn, useDataTable } from '@uhg-abyss/web/hooks/useDataTable';
import { useAuth } from '../../auth';
import { navigate } from '../../common/navigate';
import { AuthInterceptedError } from '../../common/ErrorHandler';
import { downloadSurveyExcel } from '../Home/downloadSurveyExcel';
import {
  getAllQuestionType,
  getHistoricalPoliciesByPolicyIds,
  searchHistoricalPoliciesBySearchId,
  type PolicySummary,
} from './historicalDataApi';
import {
  filterTableRows,
  getPolicyTableRows,
  splitPolicyList,
  type HistoricalTableRow,
} from './tableRows';

const FALLBACK_VALUE = 'N/A';
const DEFAULT_STATUS = 'Open';
const QUICK_FILTER_MIN_LENGTH = 3;
const PAGE_SIZE_OPTIONS = [5, 10, 20];
const YEAR_OPTION_COUNT = 7;
const YEAR_PROMPT_OPTION = { label: 'Please select', value: '' };
const NO_RECORDS_MESSAGE = 'No records found with search criteria';

const STATUS_HELP_TEXT = (
  <React.Fragment>
    <div>
      <strong>Open:</strong> RFI is not started.
    </div>
    <div>
      <strong>In Process:</strong> RFI is started, but responses have not been
      submitted to UnitedHealthcare. RFIs with status In Process are considered
      incomplete and will not be included in the submission to CMS.
    </div>
    <div>
      <strong>Complete:</strong> RFI has been successfully submitted to
      UnitedHealthcare.
    </div>
  </React.Fragment>
);

const COLUMN_HELP_TEXT = {
  userName: 'This is the name of the individual that submitted the RFI.',
  surveyStartDate: 'This is the date the RFI was started.',
  surveyCompletedDate:
    'This is the date the RFI was submitted to UnitedHealthcare.',
  download:
    "Click 'Download' to generate a form containing a prior year RFI response.",
  surveyStatus: STATUS_HELP_TEXT,
};

const Page = styled('div', {
  width: '100%',
  display: 'grid',
  gap: '1rem',
});

const Section = styled('section', {
  width: '100%',
});

const ToolbarPanel = styled(Card, {
  width: '100%',
  borderRadius: '16px',
  border: '1px solid #c8d8e6',
  backgroundColor: '$white',
  padding: '1.25rem',
  boxSizing: 'border-box',
});

const ToolbarPanelContent = styled('div', {
  display: 'grid',
  gap: '1rem',
});

const ToolbarRow = styled('div', {
  display: 'flex',
  gap: '0.75rem',
  alignItems: 'end',
  flexWrap: 'wrap',
});

const FieldLabel = styled(Text, {
  display: 'block',
  color: '$web.semantic.color.text.content.primary',
  fontWeight: 600,
  lineHeight: 1.2,
});

const InputGroup = styled('div', {
  flex: '1 1 220px',
  minWidth: 0,
  display: 'grid',
  gap: '0.35rem',
});

const SearchInputGroup = styled(InputGroup, {
  flex: '2 1 320px',
  minWidth: '260px',
});

const YearSelectGroup = styled(InputGroup, {
  flex: '0 1 180px',
  minWidth: '160px',
});

const FieldControls = styled('div', {
  display: 'grid',
  gridTemplateColumns: '1fr auto',
  gap: '0.5rem',
  alignItems: 'center',
  minWidth: 0,
});

const TextInput = styled('input', {
  width: '100%',
  minWidth: '12rem',
  minHeight: '2.5rem',
  borderRadius: '8px',
  border: '1px solid #b6c9d9',
  fontSize: '0.95rem',
  padding: '0 0.75rem',
  boxSizing: 'border-box',
  '&:focus-visible': {
    outline:
      '2px solid $web.semantic.color.surface.interactive.standards.rest.default.primary',
    outlineOffset: '1px',
  },
});

const DataTableWrapper = styled('div', {
  width: '100%',
  minWidth: 0,
  borderRadius: '12px',
  border: '1px solid #c8d8e6',
  backgroundColor: '$white',
  padding: '0',
  boxSizing: 'border-box',
  '& .abyss-data-table-root': {
    width: '100%',
    minWidth: 0,
  },
  '& table': {
    width: '100%',
    tableLayout: 'fixed',
  },
  '& th, & td': {
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
    verticalAlign: 'top',
  },
  '& .abyss-data-table-scroll': {
    overflowX: 'hidden',
    overflowY: 'hidden',
  },
  '& .abyss-data-table-slot-wrapper': {
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '0.75rem',
    flexWrap: 'wrap',
  },
  '& .abyss-data-table-pagination': {
    marginTop: 0,
    paddingTop: 0,
  },
});

const InlineLinkButton = styled('button', {
  appearance: 'none',
  border: 'none',
  background: 'none',
  padding: 0,
  margin: 0,
  color: '$web.semantic.color.text.label.cta.primary',
  textDecoration: 'underline',
  font: 'inherit',
  fontWeight: 600,
  cursor: 'pointer',
  textAlign: 'left',
  '&:focus-visible': {
    outline:
      '2px solid $web.semantic.color.surface.interactive.standards.rest.default.primary',
    outlineOffset: '2px',
  },
});

const PolicyListOverflowTrigger = styled('button', {
  appearance: 'none',
  border: 'none',
  background: 'none',
  padding: 0,
  margin: 0,
  flexShrink: 0,
  color: '$web.semantic.color.text.label.cta.primary',
  textDecoration: 'underline',
  font: 'inherit',
  cursor: 'pointer',
  '&:focus-visible': {
    outline:
      '2px solid $web.semantic.color.surface.interactive.standards.rest.default.primary',
    outlineOffset: '1px',
  },
});

const HeaderWithHelp = styled('span', {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.35rem',
});

const HelpButton = styled('button', {
  width: '1rem',
  height: '1rem',
  minWidth: '1rem',
  minHeight: '1rem',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  borderRadius: '9999px',
  border:
    '1px solid $web.semantic.color.surface.interactive.standards.rest.default.primary',
  backgroundColor:
    '$web.semantic.color.surface.interactive.standards.rest.default.primary',
  color: '$web.semantic.color.text.label.cta.primary-alt',
  fontSize: '0.7rem',
  fontWeight: 700,
  cursor: 'pointer',
});

const PolicyListCell = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '0.25rem',
  minWidth: 0,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
});

const PolicyListCellText = styled('span', {
  display: 'block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

function readPolicyIdsFromSessionStorage(): string[] {
  if (typeof window === 'undefined') {
    return [];
  }

  const rawPolicyDetails = window.sessionStorage.getItem('policy_details');
  if (!rawPolicyDetails) {
    return [];
  }

  try {
    const parsed = JSON.parse(rawPolicyDetails) as unknown;
    if (Array.isArray(parsed)) {
      return parsed.filter((value): value is string => {
        return typeof value === 'string' && value.trim().length > 0;
      });
    }

    if (typeof parsed === 'object' && parsed && 'policyIds' in parsed) {
      const policyIds = (parsed as { policyIds?: unknown }).policyIds;
      if (Array.isArray(policyIds)) {
        return policyIds.filter((value): value is string => {
          return typeof value === 'string' && value.trim().length > 0;
        });
      }
    }
  } catch {
    if (rawPolicyDetails.includes(',')) {
      return rawPolicyDetails
        .split(',')
        .map((value) => {
          return value.trim();
        })
        .filter(Boolean);
    }

    if (rawPolicyDetails.trim()) {
      return [rawPolicyDetails.trim()];
    }
  }

  return [];
}

function getDefaultHistoricalYear(now = new Date()) {
  const currentYear = now.getFullYear();
  const juneFirst = new Date(currentYear, 5, 1, 0, 0, 0, 0);

  return String(now > juneFirst ? currentYear : currentYear - 1);
}

function getHistoricalYearOptions(now = new Date()) {
  const defaultYear = Number(getDefaultHistoricalYear(now));

  return Array.from({ length: YEAR_OPTION_COUNT }, (_, index) => {
    const year = String(defaultYear - index);
    return { label: year, value: year };
  });
}

function getColumnHeader(label: string, helpText?: React.ReactNode) {
  if (!helpText) {
    return label;
  }

  return (
    <HeaderWithHelp>
      <span>{label}</span>
      <Tooltip
        content={helpText}
        position="top"
        variant="gray"
        delayDuration={200}
      >
        <HelpButton aria-label={`${label} information`} type="button">
          ?
        </HelpButton>
      </Tooltip>
    </HeaderWithHelp>
  );
}

function renderPolicyListCell(policyList: string) {
  const values = splitPolicyList(policyList, FALLBACK_VALUE);
  if (values.length <= 2) {
    return (
      <PolicyListCell>
        <PolicyListCellText>{policyList}</PolicyListCellText>
      </PolicyListCell>
    );
  }

  const visible = values.slice(0, 2).join(', ');
  const remaining = values.slice(2);

  return (
    <PolicyListCell>
      <PolicyListCellText>{visible}</PolicyListCellText>
      <Tooltip
        content={
          <React.Fragment>
            <div>{remaining.length} more value(s):</div>
            <div>{remaining.join(', ')}</div>
          </React.Fragment>
        }
        position="top"
        variant="gray"
        delayDuration={200}
      >
        <PolicyListOverflowTrigger
          type="button"
          aria-label={`Show ${remaining.length} additional policy value(s)`}
        >
          ..
        </PolicyListOverflowTrigger>
      </Tooltip>
    </PolicyListCell>
  );
}

function getHistoricalColumns(args: {
  onRowClick: (row: HistoricalTableRow) => void;
  onDownloadClick: (row: HistoricalTableRow) => void;
}) {
  return [
    createColumn<HistoricalTableRow>({
      id: 'employerGroupName',
      accessorKey: 'employerGroupName',
      header: 'Employer Group Name',
      minSize: 160,
      size: 190,
      maxSize: 230,
      cell: (context: { row: { original: HistoricalTableRow } }) => {
        return (
          <InlineLinkButton
            type="button"
            onClick={() => {
              args.onRowClick(context.row.original);
            }}
          >
            {context.row.original.employerGroupName}
          </InlineLinkButton>
        );
      },
      enableSorting: true,
    }),
    createColumn<HistoricalTableRow>({
      id: 'policyNumber',
      accessorKey: 'policyNumber',
      header: 'Policy List',
      minSize: 120,
      size: 150,
      maxSize: 190,
      cell: (context: { row: { original: HistoricalTableRow } }) => {
        return renderPolicyListCell(context.row.original.policyNumber);
      },
      enableSorting: true,
    }),
    createColumn<HistoricalTableRow>({
      id: 'surveyYear',
      accessorKey: 'surveyYear',
      header: 'Ref Year',
      minSize: 70,
      size: 80,
      maxSize: 90,
      enableSorting: true,
    }),
    createColumn<HistoricalTableRow>({
      id: 'fundingType',
      accessorKey: 'fundingType',
      header: 'Funding Type',
      minSize: 90,
      size: 100,
      maxSize: 110,
      enableSorting: true,
    }),
    createColumn<HistoricalTableRow>({
      id: 'userName',
      accessorKey: 'userName',
      header: () => {
        return getColumnHeader('Submitter Name', COLUMN_HELP_TEXT.userName);
      },
      minSize: 120,
      size: 140,
      maxSize: 180,
      enableSorting: true,
      meta: { headerLabel: 'Submitter Name' },
    }),
    createColumn<HistoricalTableRow>({
      id: 'surveyStartDate',
      accessorKey: 'surveyStartDate',
      header: () => {
        return getColumnHeader(
          'Date Started',
          COLUMN_HELP_TEXT.surveyStartDate
        );
      },
      minSize: 120,
      size: 135,
      maxSize: 150,
      enableSorting: true,
      meta: { headerLabel: 'Date Started' },
    }),
    createColumn<HistoricalTableRow>({
      id: 'surveyCompletedDate',
      accessorKey: 'surveyCompletedDate',
      header: () => {
        return getColumnHeader(
          'Date Submitted',
          COLUMN_HELP_TEXT.surveyCompletedDate
        );
      },
      minSize: 120,
      size: 135,
      maxSize: 150,
      enableSorting: true,
      meta: { headerLabel: 'Date Submitted' },
    }),
    createColumn<HistoricalTableRow>({
      id: 'surveyStatus',
      accessorKey: 'surveyStatus',
      header: () => {
        return getColumnHeader('RFI Status', COLUMN_HELP_TEXT.surveyStatus);
      },
      minSize: 100,
      size: 120,
      maxSize: 130,
      enableSorting: true,
      meta: { headerLabel: 'RFI Status' },
    }),
    createColumn<HistoricalTableRow>({
      id: 'download',
      accessorKey: 'download',
      header: () => {
        return getColumnHeader('Download RFI', COLUMN_HELP_TEXT.download);
      },
      minSize: 78,
      size: 88,
      maxSize: 98,
      enableSorting: false,
      meta: { headerLabel: 'Download RFI' },
      cell: (context: { row: { original: HistoricalTableRow } }) => {
        const row = context.row.original;
        if (row.download !== 'Download') {
          return null;
        }

        return (
          <InlineLinkButton
            type="button"
            aria-label={`Download RFI for ${row.employerGroupName}`}
            onClick={() => {
              args.onDownloadClick(row);
            }}
          >
            Download
          </InlineLinkButton>
        );
      },
    }),
  ];
}

export const HistoricalData = () => {
  const { session } = useAuth();
  const isInternalUser = session?.authType === 'INTERNAL';
  const yearOptions = React.useMemo(() => {
    return [YEAR_PROMPT_OPTION, ...getHistoricalYearOptions()];
  }, []);

  const [searchKey, setSearchKey] = React.useState('');
  const [quickFilterInput, setQuickFilterInput] = React.useState('');
  const [quickFilter, setQuickFilter] = React.useState('');
  const [selectedYear, setSelectedYear] = React.useState('');
  const [results, setResults] = React.useState<PolicySummary[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [hasSearched, setHasSearched] = React.useState(false);

  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      setQuickFilter(quickFilterInput);
    }, 500);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [quickFilterInput]);

  React.useEffect(() => {
    if (!session?.token) {
      return;
    }

    void getAllQuestionType().catch((error) => {
      if (error instanceof AuthInterceptedError) {
        return;
      }
    });
  }, [session?.token]);

  React.useEffect(() => {
    if (!session?.token || isInternalUser) {
      return;
    }

    const loadExternalRows = async () => {
      const policyIds = readPolicyIdsFromSessionStorage();
      if (policyIds.length === 0) {
        setResults([]);
        setErrorMessage(null);
        return;
      }

      setIsLoading(true);
      setErrorMessage(null);

      try {
        const data = await getHistoricalPoliciesByPolicyIds({ policyIds });
        setResults(data);
      } catch (error) {
        if (error instanceof AuthInterceptedError) {
          return;
        }
        setResults([]);
        setErrorMessage(
          error instanceof Error
            ? error.message
            : 'Unable to load historical records.'
        );
      } finally {
        setIsLoading(false);
      }
    };

    void loadExternalRows();
  }, [isInternalUser, session?.token]);

  const tableRows = React.useMemo(() => {
    return getPolicyTableRows(results, FALLBACK_VALUE, DEFAULT_STATUS);
  }, [results]);

  const yearFilteredRows = React.useMemo(() => {
    return selectedYear
      ? tableRows.filter((row) => {
          return row.rawSurveyYear === selectedYear;
        })
      : tableRows;
  }, [selectedYear, tableRows]);

  const filteredRows = React.useMemo(() => {
    return filterTableRows(
      yearFilteredRows,
      quickFilter,
      QUICK_FILTER_MIN_LENGTH
    );
  }, [quickFilter, yearFilteredRows]);

  const displayedRows = React.useMemo(() => {
    return [...filteredRows].sort((a, b) => {
      return a.employerGroupName.localeCompare(b.employerGroupName);
    });
  }, [filteredRows]);

  const handleRowNavigation = React.useCallback((row: HistoricalTableRow) => {
    if (!row.rawSurveyId) {
      return;
    }

    navigate(`/survey/entry/${row.rawSurveyId}?fromHistory=true`);
  }, []);

  const handleDownload = React.useCallback(async (row: HistoricalTableRow) => {
    const surveyId = Number.parseInt(row.rawSurveyId, 10);
    const reportingYear = Number.parseInt(row.rawSurveyYear, 10);

    if (Number.isNaN(surveyId) || Number.isNaN(reportingYear)) {
      return;
    }

    try {
      await downloadSurveyExcel({
        customerNumber: row.rawCustomerNumber,
        fundingType: row.fundingType,
        reportingYear,
        surveyId,
        employerGroupName: row.employerGroupName,
        surveyYear: row.rawSurveyYear,
      });
    } catch (error) {
      if (error instanceof AuthInterceptedError) {
        return;
      }
      setErrorMessage(
        error instanceof Error
          ? error.message
          : 'Unable to download historical RFI.'
      );
    }
  }, []);

  const columns = React.useMemo(() => {
    return getHistoricalColumns({
      onRowClick: handleRowNavigation,
      onDownloadClick: handleDownload,
    });
  }, [handleDownload, handleRowNavigation]);

  const tableState = useDataTable<HistoricalTableRow>({
    initialData: displayedRows,
    initialColumns: columns,
    rowIdKey: 'uniqueId',
    tableConfig: {
      enableSorting: true,
      enableColumnResizing: true,
      columnResizeMode: 'onEnd',
    },
    defaultSettingsConfig: {
      headerDisplaySettings: {
        hideSortingButton: false,
      },
    },
    paginationConfig: {
      enablePagination: true,
      pageSizeOptions: PAGE_SIZE_OPTIONS,
    },
  });

  React.useEffect(() => {
    tableState.setColumns(columns);
  }, [columns]);

  React.useEffect(() => {
    tableState.setData(displayedRows);
  }, [displayedRows]);

  const shouldShowNoRecords =
    !isLoading &&
    !errorMessage &&
    (isInternalUser
      ? hasSearched && displayedRows.length === 0
      : displayedRows.length === 0);

  const runInternalSearch = React.useCallback(async () => {
    const normalizedSearch = searchKey.trim();
    setHasSearched(true);

    if (!normalizedSearch) {
      setResults([]);
      setErrorMessage('Enter a search term before searching.');
      return;
    }

    if (!session?.token) {
      setResults([]);
      setErrorMessage('No authenticated session found. Please sign in again.');
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);

    try {
      const data = await searchHistoricalPoliciesBySearchId({
        searchId: normalizedSearch,
        surveyYear: selectedYear || undefined,
      });
      setResults(data);
    } catch (error) {
      if (error instanceof AuthInterceptedError) {
        return;
      }
      setResults([]);
      setErrorMessage(
        error instanceof Error
          ? error.message
          : 'Unable to load historical records.'
      );
    } finally {
      setIsLoading(false);
    }
  }, [searchKey, selectedYear, session?.token]);

  const hasSearchInput = searchKey.trim().length > 0;
  const canRunSearch = hasSearchInput && !isLoading;
  const hasQuickFilterInput = quickFilterInput.trim().length > 0;
  const canClearQuickFilter = hasQuickFilterInput && !isLoading;

  return (
    <React.Fragment>
      <Router.MetaTags title="Prior Years RFI" />
      <Page>
        {isInternalUser ? (
          <Section>
            <ToolbarPanel>
              <ToolbarPanelContent>
                <Heading level={3} size="md" textAlign="left">
                  Prior Years RFI Search
                </Heading>
                <ToolbarRow>
                  <SearchInputGroup>
                    <FieldLabel>Search historical records</FieldLabel>
                    <FieldControls>
                      <TextInput
                        aria-label="Search historical RFIs"
                        type="text"
                        value={searchKey}
                        placeholder="Search for policies by Policy number or Employer Group Name or EIN"
                        onChange={(event) => {
                          setSearchKey(event.target.value);
                        }}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.preventDefault();
                            void runInternalSearch();
                          }
                        }}
                      />
                      <Button
                        type="button"
                        variant={canRunSearch ? 'filled' : 'outline'}
                        size="sm"
                        disabled={!canRunSearch}
                        onClick={() => {
                          void runInternalSearch();
                        }}
                      >
                        Search
                      </Button>
                    </FieldControls>
                  </SearchInputGroup>
                  <InputGroup>
                    <FieldLabel>Quick filter</FieldLabel>
                    <FieldControls>
                      <TextInput
                        aria-label="Filter historical results"
                        type="text"
                        value={quickFilterInput}
                        placeholder="Filter by Employer Group Name in the List"
                        onChange={(event) => {
                          setQuickFilterInput(event.target.value);
                        }}
                      />
                      <Button
                        type="button"
                        variant={canClearQuickFilter ? 'filled' : 'outline'}
                        size="sm"
                        disabled={!canClearQuickFilter}
                        onClick={() => {
                          setQuickFilterInput('');
                        }}
                      >
                        Clear
                      </Button>
                    </FieldControls>
                  </InputGroup>
                  <YearSelectGroup>
                    <SelectInput
                      label="RFI Year"
                      value={selectedYear}
                      onChange={(value) => {
                        setSelectedYear(String(value ?? ''));
                      }}
                      options={yearOptions}
                      width="100%"
                    />
                  </YearSelectGroup>
                </ToolbarRow>

                {quickFilter.trim().length >= QUICK_FILTER_MIN_LENGTH ? (
                  <Text>{displayedRows.length} records found</Text>
                ) : null}
              </ToolbarPanelContent>
            </ToolbarPanel>
          </Section>
        ) : (
          <Section>
            <ToolbarPanel>
              <ToolbarPanelContent>
                <Heading level={3} size="md" textAlign="left">
                  Prior Years RFI Filters
                </Heading>
                <ToolbarRow>
                  <InputGroup>
                    <FieldLabel>Quick filter</FieldLabel>
                    <FieldControls>
                      <TextInput
                        aria-label="Filter historical results"
                        type="text"
                        value={quickFilterInput}
                        placeholder="Filter by Employer Group Name in the List"
                        onChange={(event) => {
                          setQuickFilterInput(event.target.value);
                        }}
                      />
                      <Button
                        type="button"
                        variant={canClearQuickFilter ? 'filled' : 'outline'}
                        size="sm"
                        disabled={!canClearQuickFilter}
                        onClick={() => {
                          setQuickFilterInput('');
                        }}
                      >
                        Clear
                      </Button>
                    </FieldControls>
                  </InputGroup>
                  <YearSelectGroup>
                    <SelectInput
                      label="RFI Year"
                      value={selectedYear}
                      onChange={(value) => {
                        setSelectedYear(String(value ?? ''));
                      }}
                      options={yearOptions}
                      width="100%"
                    />
                  </YearSelectGroup>
                </ToolbarRow>

                {quickFilter.trim().length >= QUICK_FILTER_MIN_LENGTH ? (
                  <Text>{displayedRows.length} records found</Text>
                ) : null}
              </ToolbarPanelContent>
            </ToolbarPanel>
          </Section>
        )}

        {isLoading ? <Text>Loading historical records...</Text> : null}

        {errorMessage ? (
          <Text color="$web.semantic.color.text.content.error">
            {errorMessage}
          </Text>
        ) : null}

        {shouldShowNoRecords ? <Text>{NO_RECORDS_MESSAGE}</Text> : null}

        {displayedRows.length > 0 ? (
          <Section>
            <DataTableWrapper>
              <DataTable
                title="Historical RFI records"
                hideHeader
                tableState={tableState}
              >
                <DataTable.SlotWrapper>
                  <Heading level={3} size="md" textAlign="left">
                    Historical RFI records
                  </Heading>
                  <DataTable.Pagination
                    variant="minimal"
                    showPageSizeDropdown
                  />
                </DataTable.SlotWrapper>
                <DataTable.Table />
              </DataTable>
            </DataTableWrapper>
          </Section>
        ) : null}
      </Page>
    </React.Fragment>
  );
};
